import '../components/wish-list-review';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { OffCanvasConfigs } from '../../off-canvas/interfaces/off-canvas.interface';
import { ProductCardData } from '../../product-card/product-card-display';
import { StoreState } from '../../../store/store.interface';
import { WISH_LIST_OFF_CANVAS_IDS } from '../interfaces/wish-list-off-canvas.interface';
import { WishList } from '../interfaces/wish-list-items.interface';
import { WishListDropdownConstants } from '../constants/wish-list-dropdown-constants';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { emitProductAnalyticsEvent } from '../../product-card/utils/product-card.analytics.util';
import { getCurrentWishList, getWishListsData } from '../store/wish-list.selector';
import { getImageSrcUrl } from '../../../common/utils/product-util';
import { getOffCanvasData } from '../../off-canvas/store/off-canvas.selector';
import { goToProductPage } from '../../product-card/utils/product-card.util';
import { css, html, nothing, TemplateResult } from 'lit';
import { renderOffCanvasProductLoading } from '../utils/wish-list-product.util';
import { setCurrentWishList } from '../store/wish-list.store';
import { triggerOpenOffCanvas } from '../../off-canvas/store/off-canvas.store';
import {
  getDeleteWishListConfig,
  getDownloadWishListConfig,
  getUpdateWishListConfig,
  getWishListFromEvent,
  getWishListReviewCanvasHeader,
  openShareWishListOffCanvas,
} from '../utils/wish-list-offcanvas.util';
import { getSharedWishListUrl, getWishlistShareLinkOffCanvasTitle } from '../utils/wish-list.util';
import { COMMON_OFF_CANVAS_IDS } from '../../off-canvas/interfaces/common-off-canvas.interface';
import {
  getShareLinkOffCanvasContent,
  getShareOffCanvasConfig,
} from '../../off-canvas/common-off-canvases/utils/common-off-canvas.util';
import { isOffCanvasOpening } from '../../off-canvas/utils/off-canvas.util';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { WishListAnalytics } from '../utils/wish-list-analytics.util';
import { selectUser } from '../../user-profile/user-profile.selectors';

@customElement('mhecomm-wish-list-update-product-off-canvas')
export class WishListUpdateProductOffCanvas extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .update-product-container {
        min-height: calc(100vh - 13.125rem);
      }
    `,
  ];

  /**
   * The label for the off-canvas close button.
   */
  @property()
  closeButtonLabel = 'Done';

  /**
   * The label for the create list button.
   */
  @property()
  createListButtonLabel = '+ Create a New List';

  /**
   * The title of the off-canvas.
   */
  @property()
  title = 'Add This Product to a List:';

  /**
   * The title of the nested off-canvas allowing to create a list.
   */
  @property()
  createWishListTitle = 'Create a New List';

  /**
   * The title of the nested off-canvas allowing to edit a list.
   */
  @property()
  editWishListTitle = 'Edit List Name';

  /**
   * The prefix for the delete wish list confirmation message.
   */
  @property()
  deleteWishListPrefix = 'Are you sure you want to delete your list';

  /**
   * The title of the nested off-canvas allowing to download a list.
   */
  @property()
  downloadWishListTitle = 'Save List';

  /**
   * The product retrieved from the input or Redux store.
   */
  @state()
  private product?: ProductCardData;

  /**
   * The current wish list for operations.
   */
  @state()
  private currentWishList?: WishList = undefined;

  /*
   * The current logged in user profile
   */
  @state()
  private user: UserProfile | undefined;

  /**
  * The analytics instance for the wish list.
  */
  private readonly wishListAnalytics: WishListAnalytics;

  constructor() {
    super();
    this.wishListAnalytics = new WishListAnalytics();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addTrackedEventListener(this, 'click', this.onWishlistEventTrigger);
  }

  stateChanged(state: StoreState): void {
    this.user = selectUser(state);
    this.currentWishList = getCurrentWishList(state);
    this.product = getOffCanvasData(state)?.[WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE];
    this.refreshWishListFromList(state);
    this.user = selectUser(state);

    if (isOffCanvasOpening(WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE, state)) {
      this.captureUpdateProductAnalytics(state);
    }
  }

  /**
   * Displays all the nested off-canvas components.
   */
  renderComp(): TemplateResult | typeof nothing {
    return html`
      <mhecomm-off-canvas .offCanvasConfigs="${this.getOffCanvasConfigs()}">
        <div slot="${WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE}-off-canvas-body">
          ${this.renderMainOffCanvasBody()}
        </div>
        <div slot="${WISH_LIST_OFF_CANVAS_IDS.CREATE_WISH_LIST}-off-canvas-body">
          ${this.renderCreateUpdateWishList(WISH_LIST_OFF_CANVAS_IDS.CREATE_WISH_LIST, false)}
        </div>
        <div slot="update-list-from-update-product-off-canvas-body">
          ${this.renderCreateUpdateWishList(WISH_LIST_OFF_CANVAS_IDS.UPDATE_WISH_LIST, true)}
        </div>
        <div slot="${WISH_LIST_OFF_CANVAS_IDS.DELETE_WISH_LIST}-off-canvas-body">
          ${this.renderDeleteWishList(WISH_LIST_OFF_CANVAS_IDS.DELETE_WISH_LIST)}
        </div>
        <div slot="${WISH_LIST_OFF_CANVAS_IDS.DOWNLOAD_WISH_LIST_ITEMS}-off-canvas-body">
          ${this.renderDownloadWishList(WISH_LIST_OFF_CANVAS_IDS.DOWNLOAD_WISH_LIST_ITEMS)}
        </div>
        <div slot="${WISH_LIST_OFF_CANVAS_IDS.REVIEW_WISH_LIST}-off-canvas-body">
          <mhecomm-review-wish-list
            .wishListData=${this.currentWishList}
          ></mhecomm-review-wish-list>
        </div>
        <div slot="${WISH_LIST_OFF_CANVAS_IDS.UPDATE_WISH_LIST_FROM_REVIEW}-off-canvas-body">
          ${this.renderCreateUpdateWishList(WISH_LIST_OFF_CANVAS_IDS.UPDATE_WISH_LIST_FROM_REVIEW, true)}
        </div>
        <div slot="${WISH_LIST_OFF_CANVAS_IDS.DELETE_WISH_LIST_FROM_REVIEW}-off-canvas-body">
          ${this.renderDeleteWishList(WISH_LIST_OFF_CANVAS_IDS.DELETE_WISH_LIST_FROM_REVIEW)}
        </div>
        <div slot="${WISH_LIST_OFF_CANVAS_IDS.DOWNLOAD_WISH_LIST_FROM_REVIEW}-off-canvas-body">
          ${this.renderDownloadWishList(WISH_LIST_OFF_CANVAS_IDS.DOWNLOAD_WISH_LIST_FROM_REVIEW)}
        </div>
        <div slot="${COMMON_OFF_CANVAS_IDS.COPY_AND_SHARE_LINK}-off-canvas-body">
          ${this.renderCopyAndShareLink()}
        </div>
      </mhecomm-off-canvas>
    `;
  }

  /**
   * Renders the main off-canvas body, allowing to update the product on the wish list.
   */
  private renderMainOffCanvasBody(): TemplateResult {
    return html` <div class="position-relative">
      <div class="update-product-container">
        ${this.renderProduct()}
        <div class="pt-3">
          <mhecomm-wish-list-update-product-comment
            .offCanvasId="${WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE}"
            .product="${this.product}"
          ></mhecomm-wish-list-update-product-comment>
        </div>
        <hr class="my-3" />
        <mhecomm-wish-list-update-product
          .offCanvasId="${WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE}"
          .product="${this.product}"
        ></mhecomm-wish-list-update-product>
      </div>
      <div class="bg-white py-3 position-sticky bottom-0 w-100">
        <button
          id="wish-list-create-button"
          data-automation-id="wish-list-create-button"
          type="button"
          class="btn btn-outline-primary mb-2 w-100"
        >
          ${this.createListButtonLabel}
        </button>
        <button
          id="wish-list-product-done-button"
          data-automation-id="wish-list-product-done-button"
          type="button"
          class="btn btn-primary w-100"
        >
          ${this.closeButtonLabel}
        </button>
      </div>
    </div>`;
  }

  /**
   * Renders the create/update wish list component for the off-canvas.
   *
   * @param offCanvasId
   * @param update
   */
  private renderCreateUpdateWishList(offCanvasId: string, update: boolean): TemplateResult {
    if (update) {
      return html`
        <mhecomm-create-wish-list
          offCanvasId="${offCanvasId}"
          wishListId=${this.currentWishList?.wishlist_id!}
          wishListName=${this.currentWishList?.name!}
          ?isEditWishList=${true}
        ></mhecomm-create-wish-list>
      `;
    }

    return html` <mhecomm-create-wish-list offCanvasId="${offCanvasId}"></mhecomm-create-wish-list> `;
  }

  /**
   * Renders the delete wish list component for the off-canvas.
   *
   * @param offCanvasId
   */
  private renderDeleteWishList(offCanvasId: string): TemplateResult {
    return html`
      <mhecomm-delete-wish-list
        offCanvasId="${offCanvasId}"
        wishListId="${this.currentWishList?.wishlist_id!}"
        wishListName="${this.currentWishList?.name!}"
      >
      </mhecomm-delete-wish-list>
    `;
  }

  /**
   * Renders the download wish list component for the off-canvas.
   *
   * @param offCanvasId
   */
  private renderDownloadWishList(offCanvasId: string): TemplateResult {
    return html`
      <mhecomm-download-wish-list
        offCanvasId="${offCanvasId}"
        .wishList=${this.currentWishList}
      ></mhecomm-download-wish-list>
    `;
  }

  private renderCopyAndShareLink(): TemplateResult {
    return getShareLinkOffCanvasContent(
      getWishlistShareLinkOffCanvasTitle(this.currentWishList?.name!),
      getSharedWishListUrl(this.currentWishList?.sharing_code!),
      true,
      false,
      () => this.wishListAnalytics.shareWishList(
        this, 
        this.currentWishList?.wishlist_id!, 
        this.currentWishList?.name!, 
        getSharedWishListUrl(this.currentWishList?.sharing_code ?? ''), 
        this.user!
      )!
    );
  }

  /**
   * Returns the off-canvas list for the component.
   *
   * This configuration is updated based on the states.
   */
  private getOffCanvasConfigs(): OffCanvasConfigs {
    return {
      [WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE]: {
        id: WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE,
        title: html`<h5 class="mb-0">${this.title}</h5>`,
        nextTriggers: [
          {
            triggerId: 'wish-list-create-button',
            offCanvasId: WISH_LIST_OFF_CANVAS_IDS.CREATE_WISH_LIST,
          },
        ],
        closeTriggerId: 'wish-list-product-done-button',
        bodyStyle: 'px-3 pt-3 pb-0',
      },
      [WISH_LIST_OFF_CANVAS_IDS.CREATE_WISH_LIST]: {
        id: WISH_LIST_OFF_CANVAS_IDS.CREATE_WISH_LIST,
        title: html`<h5 class="mb-0">${this.createWishListTitle}</h5>`,
        closeTriggerId: 'wish-list-product-done-button',
        focusElement: '#wishListName',
        prevTrigger: {
          triggerId: '#cancelWishlistCreationBtn',
          offCanvasId: WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE,
        },
      },
      [WISH_LIST_OFF_CANVAS_IDS.UPDATE_WISH_LIST]: getUpdateWishListConfig(
        WISH_LIST_OFF_CANVAS_IDS.UPDATE_WISH_LIST,
        WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE,
        this.editWishListTitle
      ),
      [WISH_LIST_OFF_CANVAS_IDS.DELETE_WISH_LIST]: getDeleteWishListConfig(
        WISH_LIST_OFF_CANVAS_IDS.DELETE_WISH_LIST,
        WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE,
        this.deleteWishListPrefix,
        this.currentWishList
      ),
      [WISH_LIST_OFF_CANVAS_IDS.DOWNLOAD_WISH_LIST_ITEMS]: getDownloadWishListConfig(
        WISH_LIST_OFF_CANVAS_IDS.DOWNLOAD_WISH_LIST_ITEMS,
        WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE,
        this.downloadWishListTitle
      ),
      [WISH_LIST_OFF_CANVAS_IDS.REVIEW_WISH_LIST]: {
        id: WISH_LIST_OFF_CANVAS_IDS.REVIEW_WISH_LIST,
        prevTrigger: {
          offCanvasId: WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE,
        },
        subtitle: getWishListReviewCanvasHeader(this.currentWishList),
        bodyStyle: 'p-0',
      },
      [WISH_LIST_OFF_CANVAS_IDS.UPDATE_WISH_LIST_FROM_REVIEW]: getUpdateWishListConfig(
        WISH_LIST_OFF_CANVAS_IDS.UPDATE_WISH_LIST_FROM_REVIEW,
        WISH_LIST_OFF_CANVAS_IDS.REVIEW_WISH_LIST,
        this.editWishListTitle
      ),
      [WISH_LIST_OFF_CANVAS_IDS.DELETE_WISH_LIST_FROM_REVIEW]: getDeleteWishListConfig(
        WISH_LIST_OFF_CANVAS_IDS.DELETE_WISH_LIST_FROM_REVIEW,
        WISH_LIST_OFF_CANVAS_IDS.REVIEW_WISH_LIST,
        this.deleteWishListPrefix,
        this.currentWishList
      ),
      [WISH_LIST_OFF_CANVAS_IDS.DOWNLOAD_WISH_LIST_FROM_REVIEW]: getDownloadWishListConfig(
        WISH_LIST_OFF_CANVAS_IDS.DOWNLOAD_WISH_LIST_FROM_REVIEW,
        WISH_LIST_OFF_CANVAS_IDS.REVIEW_WISH_LIST,
        this.downloadWishListTitle
      ),
      [COMMON_OFF_CANVAS_IDS.COPY_AND_SHARE_LINK]: getShareOffCanvasConfig(
        getWishlistShareLinkOffCanvasTitle(this.currentWishList?.name ?? '')
      ),
    };
  }

  /**
   * Renders a smaller version of the product card specific to the off-canvas.
   */
  private renderProduct(): TemplateResult {
    if (!this.product) {
      return renderOffCanvasProductLoading();
    }

    return html`
      <div class="w-25">
        <mhecomm-product-card-image-display
          productTitle="${this.product?.title}"
          productImageUrl="${getImageSrcUrl(this.product?.isbn!)}"
          containerStyle="pb-3"
          imageStyle="shadow-lg w-75"
        >
        </mhecomm-product-card-image-display>
      </div>
      <mhecomm-product-card-metadata-display
        .product="${this.product}"
        cta="noCta"
        ?showBusinessLabel=${false}
        ?showBusinessLabelType=${false}
        ?showProgramFamily=${true}
        ?showMHID=${true}
        ?showGrades=${true}
        ?showEdition=${true}
        ?showCopyright=${true}
        .titleClickListener=${(): void => {
          emitProductAnalyticsEvent(this, this.product!, this.product!.businessUnit.toLowerCase());
          goToProductPage(this.product!);
        }}
      >
      </mhecomm-product-card-metadata-display>
    `;
  }

  /**
   * Handles wish lists events.
   *
   * @param event
   */
  private onWishlistEventTrigger(event: CustomEventInit): void {
    switch (event?.detail?.eventName) {
      case 'wish-list-option-selection':
        return this.handleDropdownOffCanvasEvents(event);
      case 'wish-list-review':
        return this.handleWishListReviewEvent(event);
      default:
        break;
    }
  }

  /**
   * Handles the event to open the review the wish list panel.
   *
   * @param event
   */
  private handleWishListReviewEvent(event: CustomEventInit): void {
    const wishList = getWishListFromEvent(event);
    if (wishList) {
      store.dispatch(setCurrentWishList(wishList));
      store.dispatch(
        triggerOpenOffCanvas({
          offCanvasId: WISH_LIST_OFF_CANVAS_IDS.REVIEW_WISH_LIST,
        })
      );
    }
  }

  /**
   * Handles the event to open the matching off-canvas panel based on the option.
   *
   * @param event
   */
  private handleDropdownOffCanvasEvents(event: CustomEventInit): void {
    const wishList = getWishListFromEvent(event);
    if (!wishList) {
      return;
    }

    store.dispatch(setCurrentWishList(wishList));
    const isTriggeredFromReview = event?.detail?.id?.includes(WISH_LIST_OFF_CANVAS_IDS.REVIEW_WISH_LIST);

    switch (event?.detail?.option) {
      case WishListDropdownConstants.EDIT_NAME:
        store.dispatch(
          triggerOpenOffCanvas({
            offCanvasId: isTriggeredFromReview
              ? WISH_LIST_OFF_CANVAS_IDS.UPDATE_WISH_LIST_FROM_REVIEW
              : WISH_LIST_OFF_CANVAS_IDS.UPDATE_WISH_LIST,
          })
        );
        break;
      case WishListDropdownConstants.DELETE:
        store.dispatch(
          triggerOpenOffCanvas({
            offCanvasId: isTriggeredFromReview
              ? WISH_LIST_OFF_CANVAS_IDS.DELETE_WISH_LIST_FROM_REVIEW
              : WISH_LIST_OFF_CANVAS_IDS.DELETE_WISH_LIST,
          })
        );
        break;
      case WishListDropdownConstants.DOWNLOAD:
        store.dispatch(
          triggerOpenOffCanvas({
            offCanvasId: isTriggeredFromReview
              ? WISH_LIST_OFF_CANVAS_IDS.DOWNLOAD_WISH_LIST_FROM_REVIEW
              : WISH_LIST_OFF_CANVAS_IDS.DOWNLOAD_WISH_LIST_ITEMS,
          })
        );
        break;
      case WishListDropdownConstants.SHARE:
        openShareWishListOffCanvas(wishList.wishlist_id);
        break;
      default:
        break;
    }
  }

  /**
   * Refreshes the current wish list if the collection of wish list changes.
   */
  private refreshWishListFromList(state: StoreState): void {
    const wishLists = getWishListsData(state);
    if (wishLists && wishLists.items) {
      for (const wishList of wishLists.items) {
        if (wishList.wishlist_id === this.currentWishList?.wishlist_id) {
          this.currentWishList = wishList;
          break;
        }
      }
    }
  }

  private captureUpdateProductAnalytics(state: StoreState): void {
    const product = getOffCanvasData(state)?.[WISH_LIST_OFF_CANVAS_IDS.PRODUCT_UPDATE];
    const wishLists: WishList[] = getWishListsData(state)?.items ?? [];
    this.wishListAnalytics.updateProductWishLists(
      this,
      wishLists.filter((wishlist) => {
        return wishlist.wishlist_items.some((item) => item.mhid === product.mhid);
      }),
      wishLists.reduce((acc, wishlist) => {
        const wishlistCommentMap = {};
        wishlist.wishlist_items.some((item) => {
          if (item.mhid === product.mhid) {
            wishlistCommentMap[wishlist.wishlist_id] = item.comments;
            return true;
          }
          return false;
        });
        return {
          ...wishlistCommentMap,
          ...acc,
        };
      }, {}),
      this.user!,
      product,
      'wish-list',
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-wish-list-update-product-off-canvas': WishListUpdateProductOffCanvas;
  }
}